import React from "react";
import cx from "classnames";
import { HighlightText } from "components";
import { useTranslate } from "hooks";
import s from "./ProductsOverview.module.scss";

export type SearchResultItem = {
  key?: string;
  name?: string | null;
  parentCategories?: string | string[];
  isSerie?: boolean;
};

type SearchResultsProps = {
  isModalActive: boolean;
  results: SearchResultItem[];
  title: string;
  query: string;
  handleClick: (item: SearchResultItem, isSerie?: boolean) => void;
};

const matchesQuery = (item: SearchResultItem, search: string): boolean => {
  if (item.name && item.name.toLowerCase().includes(search)) return true;
  if (item.key && item.key.toLowerCase().includes(search)) return true;
  if (
    Array.isArray(item.parentCategories) &&
    item.parentCategories.some(
      (cat) => typeof cat === "string" && cat.toLowerCase().includes(search),
    )
  )
    return true;
  return false;
};

export const SearchResults = ({
  isModalActive,
  results,
  title,
  query,
  handleClick,
}: SearchResultsProps) => {
  const translate = useTranslate();
  const search = query.toLowerCase();
  let filtered: SearchResultItem[] = [];

  if (isModalActive) {
    filtered = results.filter((item) => matchesQuery(item, search));
  } else {
    filtered = results
      .filter((item) => matchesQuery(item, search))
      .map((item) => {
        const matchedCategory = Array.isArray(item.parentCategories)
          ? item.parentCategories.find(
              (cat) =>
                typeof cat === "string" && cat.toLowerCase().includes(search),
            ) || item.parentCategories[item.parentCategories.length - 1]
          : null;

        return matchedCategory
          ? { ...item, parentCategories: matchedCategory }
          : item;
      });
  }

  return (
    <div className={cx(s.searchResultsCol, "text-gray-700")}>
      <div className="d-flex flex-row">
        <div
          className={cx(
            s.searchResultsTitle,
            "py-2 mb-2",
            isModalActive ? "col-12" : "col-5",
          )}
        >
          {title}
        </div>
        {!isModalActive && (
          <div className={cx(s.searchResultsTitle, "py-2 mb-2 col-7")}>
            {translate("PRODUCT_SEARCH_CATEGORIES_RESULTS_TITLE")}
          </div>
        )}
      </div>

      {filtered.map((item, index) => (
        <div key={`${item.name}-${index}`} className="d-flex flex-row">
          <button
            className={cx(
              s.searchResultsName,
              "p-2 pe-0 text-truncate border-0 bg-transparent text-start",
              isModalActive ? "col-12" : "col-5",
            )}
            onClick={() => handleClick(item, item.isSerie)}
          >
            <HighlightText
              highlightTag="mark"
              className="font-weight-light text-gray-700"
              symbolsToHighlight={query.split(/\s+/)}
              text={item.name ?? ""}
            />
          </button>
          {!isModalActive && (
            <button
              className={cx(
                s.searchResultsName,
                "p-2 pe-0 text-truncate col-7 border-0 bg-transparent text-start",
              )}
              onClick={() => handleClick(item)}
            >
              <HighlightText
                highlightTag="mark"
                className="font-weight-light text-gray-700"
                symbolsToHighlight={query.split(/\s+/)}
                text={
                  typeof item.parentCategories === "string"
                    ? item.parentCategories
                    : (item.parentCategories?.[0] ?? "")
                }
              />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};
